import { DATE_FILTER_SUPPORTED_PAGE_PATHS, ROUTE_CONFIG } from '@/config/Routes/routes.config';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';

export type GroupByValue = 'day' | 'week' | 'month' | 'year';

export interface DateFilterValues {
  startDate: string | Date | undefined;
  endDate: string | Date | undefined;
}

export interface DateFilterContextType {
  showDateFilter: boolean;
  toggleDateFilter: () => void;
  isVisible: boolean;
  filterValues: DateFilterValues;
  setFilterValues: (values: DateFilterValues) => void;
  isDashboard: boolean;
  /** Active groupBy granularity — only meaningful on the dashboard */
  groupBy: GroupByValue;
  setGroupBy: (v: GroupByValue) => void;
}

export const DateFilterContext = createContext<
  DateFilterContextType | undefined
>(undefined);

export const DateFilterContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [showDateFilter, setShowDateFilter] = useState(false);
  const location = useLocation();

  const [filterValues, setFilterValues] = useState<DateFilterValues>({
    startDate: '',
    endDate: '',
  });
  const [groupBy, setGroupBy] = useState<GroupByValue>('day');
  const toggleDateFilter = useCallback(() => {
    setShowDateFilter((prev) => !prev);
  }, []);

  const isVisible = DATE_FILTER_SUPPORTED_PAGE_PATHS.includes(
    location.pathname,
  );
  const isDashboard =
    location.pathname === `/${ROUTE_CONFIG.DASHBOARD.path}`;

  useEffect(() => {
    setShowDateFilter(false);
  }, [location.pathname]);

  return (
    <DateFilterContext.Provider
      value={{
        showDateFilter,
        toggleDateFilter,
        isVisible,
        filterValues,
        setFilterValues,
        isDashboard,
        groupBy,
        setGroupBy,
      }}
    >
      {children}
    </DateFilterContext.Provider>
  );
};

export const useDateFilter = () => {
  const context = useContext(DateFilterContext);
  if (!context)
    throw new Error(
      'useDateFilter must be used within a DateFilterContextProvider',
    );
  return context;
};
